import React from 'react'
import { motion } from 'framer-motion'
import { 
  Mic, 
  Smartphone, 
  Monitor, 
  Globe, 
  Zap, 
  GitBranch, 
  Search, 
  Link,
  Layers,
  Clock,
  Shield,
  Workflow
} from 'lucide-react'

const Features: React.FC = () => {
  const features = [
    {
      icon: Mic,
      title: "Voice-First Capture",
      description: "One-tap voice recording on Apple Watch. Capture ideas instantly, even without your phone.",
      category: "Capture"
    },
    {
      icon: Layers,
      title: "Status Progression",
      description: "Visual tracking from Seed → Sprout → Growing → Mature. Watch your ideas evolve.",
      category: "Organization"
    },
    {
      icon: Workflow,
      title: "Multi-Device Workflow",
      description: "Start on Watch, develop on iPhone, write on Mac. Seamless handoff between devices.",
      category: "Workflow"
    },
    {
      icon: GitBranch,
      title: "GitHub Integration",
      description: "One-click publishing to your blog or repository. Turn ideas into published content.",
      category: "Publishing"
    },
    {
      icon: Search,
      title: "Intelligent Search",
      description: "Full-text search across all your ideas. Find connections and related thoughts instantly.",
      category: "Discovery"
    },
    {
      icon: Link,
      title: "Idea Linking",
      description: "Connect related thoughts and build your personal knowledge graph.",
      category: "Organization"
    },
    {
      icon: Zap,
      title: "Instant Sync",
      description: "CloudKit integration ensures your ideas are available everywhere, instantly.",
      category: "Sync"
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "Your ideas stay yours. Local-first with optional cloud sync. No tracking.",
      category: "Privacy"
    },
    {
      icon: Clock,
      title: "Time Context",
      description: "Automatic timestamps and location tracking help you remember the context.",
      category: "Context"
    }
  ]

  const platforms = [
    {
      icon: Smartphone,
      name: "iOS",
      description: "Full-featured mobile app with voice recording, editing, and management",
      status: "Available"
    },
    {
      icon: Mic,
      name: "watchOS",
      description: "Quick voice capture and recent ideas overview",
      status: "Available"
    },
    {
      icon: Monitor,
      name: "macOS",
      description: "Advanced editing, batch operations, and publishing tools",
      status: "Available"
    },
    {
      icon: Globe,
      name: "Web",
      description: "Cross-platform access with PWA support",
      status: "Available"
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  }

  return (
    <section id="features" className="section-padding bg-white">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Powerful <span className="gradient-text">Features</span> for Creators
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to capture, develop, and publish your ideas. Built for the modern creative workflow.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-gray-50 rounded-xl p-6 card-hover group"
            >
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-primary-200 transition-colors">
                  <feature.icon className="text-primary-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  <span className="text-sm text-primary-600 font-medium">{feature.category}</span>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Platform Support */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary-50 to-green-50 rounded-2xl p-8"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">
              <span className="gradient-text">Universal</span> Platform Support
            </h3>
            <p className="text-lg text-gray-600">
              Native apps for every device in your workflow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 text-center shadow-sm card-hover"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <platform.icon className="text-primary-600" size={28} />
                </div>
                <h4 className="text-xl font-semibold mb-2">{platform.name}</h4>
                <p className="text-gray-600 text-sm mb-3">{platform.description}</p>
                <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  {platform.status}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Feature Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">
              The Complete <span className="gradient-text">Creative Pipeline</span>
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-8">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🌱</span>
                </div>
                <span className="font-medium">Seed</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🌿</span>
                </div>
                <span className="font-medium">Sprout</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🌳</span>
                </div>
                <span className="font-medium">Growing</span>
              </div>
              <div className="hidden md:block text-gray-400">→</div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">🍎</span>
                </div>
                <span className="font-medium">Mature</span>
              </div>
            </div>
            <p className="text-gray-600 mt-4">
              Watch your ideas grow from initial thoughts to published content with visual status tracking
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Features
